// Admin-managed status colour palette for the Lanes module.
//
// The 14 status slugs and their built-in defaults are code-owned. This
// module layers a BE-stored override per slug (only the "bar" / primary
// colour) and derives the matching pill background, border, and text
// from that single colour so admins only ever pick one value per status.
//
// Apply path:
//   bootstrapStatusColours()  — called once at app boot from App.tsx
//     1. read localStorage cache, apply CSS vars synchronously (no
//        flash of defaults before the BE fetch resolves);
//     2. fetch overrides from /admin/lane-statuses, refresh the cache,
//        and re-apply.
//
// All consumers (LaneStatusPill, LaneTable, LaneFloor, LaneKpiStrip)
// read --status-{slug}-{bg|fg|border|bar} via var() so overriding on
// documentElement cascades automatically — no JSX change needed.

import { STATUS_SLUGS, type StatusSlug } from "./laneStatus";
import { apiFetch } from "../../api/apiFetch";

// Mirror of --status-{slug}-bar in design-tokens.css. If a token there
// ever changes, update this map in the same commit.
export const DEFAULT_STATUS_COLOURS: Record<StatusSlug, string> = {
    "lane-empty": "#CBD2D9",
    "greens": "#34A853",
    "oranges": "#F0A04B",
    "pinks": "#E25A9A",
    "greys": "#6B7884",
    "greens-outbound": "#34A853",
    "oranges-outbound": "#F0A04B",
    "apc": "#2F7CC8",
    "auction-pallets-outbound": "#2F7CC8",
    "ready-for-auction": "#7C66E3",
    "auctions-filling": "#6D52D9",
    "on-auction": "#5A3FCC",
    "auctions-ended": "#D63C58",
    "unprocessed-stock": "#21262D",
};

export interface StatusColourQuad {
    bg: string;
    fg: string;
    border: string;
    bar: string;
}

// ---- colour maths ----

const clamp = (n: number, lo: number, hi: number): number =>
    n < lo ? lo : n > hi ? hi : n;

const parseHex = (hex: string): [number, number, number] => {
    const s = hex.startsWith("#") ? hex.slice(1) : hex;
    return [
        parseInt(s.slice(0, 2), 16),
        parseInt(s.slice(2, 4), 16),
        parseInt(s.slice(4, 6), 16),
    ];
};

const toHex = (r: number, g: number, b: number): string => {
    const h = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
};

interface HSL { h: number; s: number; l: number; }

const rgbToHsl = (r: number, g: number, b: number): HSL => {
    const R = r / 255, G = g / 255, B = b / 255;
    const max = Math.max(R, G, B), min = Math.min(R, G, B);
    const l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case R: h = ((G - B) / d + (G < B ? 6 : 0)); break;
            case G: h = ((B - R) / d + 2); break;
            case B: h = ((R - G) / d + 4); break;
        }
        h /= 6;
    }
    return { h, s, l };
};

const hslToHex = (h: number, s: number, l: number): string => {
    if (s === 0) {
        const v = Math.round(l * 255);
        return toHex(v, v, v);
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue2rgb = (t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return toHex(hue2rgb(h + 1 / 3) * 255, hue2rgb(h) * 255, hue2rgb(h - 1 / 3) * 255);
};

// Derive the four-quad from a single picked primary. Hue stays
// constant; saturation is dialled down for the pale shades so they
// read as tinted neutrals rather than washed-out brights.
//
// Special case: very dark picks (lightness < 0.25) become a filled
// chip with white text — preserves the "Unprocessed Stock" look when
// admins pick near-black values.
export const deriveColours = (hex: string): StatusColourQuad => {
    const [r, g, b] = parseHex(hex);
    const { h, s, l } = rgbToHsl(r, g, b);
    const bar = hex.toUpperCase();
    if (l < 0.25) {
        return { bg: bar, fg: "#FFFFFF", border: bar, bar };
    }
    return {
        bar,
        bg:     hslToHex(h, Math.min(s, 0.40), 0.95),
        border: hslToHex(h, Math.min(s, 0.55), 0.85),
        fg:     hslToHex(h, Math.min(s + 0.10, 0.80), 0.28),
    };
};

// ---- cache (localStorage, for synchronous boot-time apply) ----

const CACHE_KEY = "lanes-status-colours-cache";

const isStatusSlug = (s: string): s is StatusSlug =>
    (STATUS_SLUGS as readonly string[]).includes(s);

export const readCachedOverrides = (): Partial<Record<StatusSlug, string>> => {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return {};
        const out: Partial<Record<StatusSlug, string>> = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (isStatusSlug(k) && typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v)) {
                out[k] = v.toUpperCase();
            }
        }
        return out;
    } catch {
        return {};
    }
};

export const writeCachedOverrides = (o: Partial<Record<StatusSlug, string>>): void => {
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(o));
    } catch { /* storage disabled */ }
};

export const clearCachedOverrides = (): void => {
    try { window.localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
};

// ---- apply ----

// Writes every status's four CSS variables to documentElement so the
// whole module rethemes in place. Cheap and idempotent (~56 setProperty
// calls) — safe to run on every edit.
export const applyStatusColours = (overrides: Partial<Record<StatusSlug, string>>): void => {
    const root = document.documentElement;
    for (const slug of STATUS_SLUGS) {
        const pick = overrides[slug] ?? DEFAULT_STATUS_COLOURS[slug];
        const { bg, fg, border, bar } = deriveColours(pick);
        root.style.setProperty(`--status-${slug}-bg`, bg);
        root.style.setProperty(`--status-${slug}-fg`, fg);
        root.style.setProperty(`--status-${slug}-border`, border);
        root.style.setProperty(`--status-${slug}-bar`, bar);
    }
};

// ---- BE I/O ----

interface OverrideRow { slug: string; bar_colour: string; }

export const fetchOverrides = async (): Promise<Partial<Record<StatusSlug, string>>> => {
    const res = await apiFetch(`/admin/lane-statuses`, {});
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const rows: OverrideRow[] = await res.json();
    const out: Partial<Record<StatusSlug, string>> = {};
    for (const row of rows) {
        if (isStatusSlug(row.slug) && typeof row.bar_colour === "string") {
            out[row.slug] = row.bar_colour.toUpperCase();
        }
    }
    return out;
};

export const patchOverride = async (slug: StatusSlug, hex: string): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bar_colour: hex }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const resetOverride = async (slug: StatusSlug): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses/${slug}`, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const resetAllOverrides = async (): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses`, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

// ---- boot ----

// Apply cached overrides synchronously (no flash of defaults), then
// fetch the BE source-of-truth in the background. On fetch success the
// cache is refreshed and the palette re-applied. On failure the cache
// stays applied — Lanes never blanks.
export const bootstrapStatusColours = (): void => {
    applyStatusColours(readCachedOverrides());
    void fetchOverrides().then(latest => {
        writeCachedOverrides(latest);
        applyStatusColours(latest);
    }).catch(() => { /* keep cached palette */ });
};

